import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppState } from '../../../../redux/reducers';
import * as ContractReviewStore from '../../../../redux/middleware/ContractReview.Middleware';
import * as FileUploadStore from '../../../../redux/middleware/FileUploadMiddleware';
import { ContractAddendum } from '../../../../models/Entities/Contracts/ContractAddendum';
import { FileUpload } from '../../../../models/Entities/Contracts/FileUpload';
import { ContractReview, ContractReviewMode } from '../../../../models/Entities/Contracts/ContractReview';
import { ErrorType } from '../../../../models/HttpError';
import { isReviewActive } from '../ContractReview.Functions';
import { ContractReviewLauncher, LauncherInitialValues } from './ContractReviewLauncher.Component';

// Lanzador conectado: botones "Revisar con IA" / "Nueva adenda con IA" del widget de importación (D-6) y su drawer.

interface OwnProps {
  mode: ContractReviewMode;
  contractId: number;
  addendum?: ContractAddendum;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialValues?: LauncherInitialValues;
  showTrigger?: boolean;
}

interface ConnectedProps {
  addendums: ContractAddendum[];
  files: FileUpload[];
  isLoadingFiles: boolean;
  isCreating: boolean;
  successCreating: boolean | undefined;
  createError?: ErrorType;
  activeReviewId?: string;
  contractNumber?: string;
  reviews: ContractReview[];
  isLoadingReviews: boolean;
  reviewsError?: ErrorType;
}

interface DispatchProps {
  CreateContractReview: typeof ContractReviewStore.actionCreators.CreateContractReview;
  ClearCreateStatus: typeof ContractReviewStore.actionCreators.ClearCreateStatus;
  GetAllFileUploads: typeof FileUploadStore.actionCreators.GetAllFileUploads;
  GetContractReviews: typeof ContractReviewStore.actionCreators.GetContractReviews;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

/** Los mismos filtros que la lista de archivos del detalle: pedirlos no cambia lo que esa lista muestra. */
export const CONTRACT_FILES_FILTERS = { page: 1, pageSize: 20, entityType: 'contract' };

export const mapStateToProps = (state: AppState, own: OwnProps): ConnectedProps => {
  const files = (state.FileUpload?.FileUploads ?? []).filter(f => !f.contractId || f.contractId === own.contractId || f.entityId === own.contractId);
  const addendumId = own.mode === 'Review' ? own.addendum?.id ?? null : undefined;
  const list = state.ContractReview?.listByContract?.[own.contractId];
  const contract = state.Contract?.list?.find(c => c.id === own.contractId);
  const active = (list?.items ?? []).find(
    r => isReviewActive(r) && (addendumId === undefined || r.addendumId === addendumId)
  );
  return {
    addendums: contract?.addendums ?? [],
    files,
    isLoadingFiles: !!state.FileUpload?.isLoadingAll,
    isCreating: !!state.ContractReview?.isCreating,
    successCreating: state.ContractReview?.successCreating,
    createError: state.ContractReview?.createError,
    activeReviewId: active?.id,
    contractNumber: contract?.contractNumber,
    reviews: list?.items ?? [],
    isLoadingReviews: !!list?.isLoading,
    reviewsError: list?.error
  };
};

export const mapDispatchToProps = {
  CreateContractReview: ContractReviewStore.actionCreators.CreateContractReview,
  ClearCreateStatus: ContractReviewStore.actionCreators.ClearCreateStatus,
  GetAllFileUploads: FileUploadStore.actionCreators.GetAllFileUploads,
  GetContractReviews: ContractReviewStore.actionCreators.GetContractReviews
};

export const ContractReviewLauncherContainer: React.FC<Props> = props => {
  const navigate = useNavigate();
  const { contractId } = props;

  const onLoadFiles = useCallback(
    () => props.GetAllFileUploads({ ...CONTRACT_FILES_FILTERS, entityId: contractId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractId]
  );

  return (
    <ContractReviewLauncher
      mode={props.mode}
      contractId={contractId}
      contractNumber={props.contractNumber}
      addendum={props.addendum}
      addendums={props.addendums}
      files={props.files}
      isLoadingFiles={props.isLoadingFiles}
      isCreating={props.isCreating}
      successCreating={props.successCreating}
      createError={props.createError}
      activeReviewId={props.activeReviewId}
      reviews={props.reviews}
      isLoadingReviews={props.isLoadingReviews}
      reviewsError={props.reviewsError}
      onReloadReviews={() => props.GetContractReviews(contractId)}
      onLoadFiles={onLoadFiles}
      onCreate={command => props.CreateContractReview(contractId, command)}
      onViewReview={reviewId => navigate(`/contracts/${contractId}/reviews/${reviewId}`)}
      onClearCreateStatus={() => props.ClearCreateStatus()}
      open={props.open}
      onOpenChange={props.onOpenChange}
      initialValues={props.initialValues}
      showTrigger={props.showTrigger}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ContractReviewLauncherContainer as any) as unknown as React.FC<OwnProps>;
